/**
 * Pure helpers for <XtsViewer3D>. Lives outside the component file so the
 * top-level component can focus on the React + r3f wiring.
 */

import {
  ACESFilmicToneMapping,
  AgXToneMapping,
  Box3,
  CineonToneMapping,
  LinearToneMapping,
  Matrix4,
  NoToneMapping,
  ReinhardToneMapping,
  type ToneMapping,
  Vector3,
} from 'three';
import { buildChain, moverWorldAt } from '../../geometry/ChainBuilder.js';
import {
  orientationMatrix,
  partTransformationMatrix,
  trackTransformMatrix,
} from '../../geometry/trackTransform.js';
import { sampleChainRange } from '../../geometry/sampleChain.js';
import {
  chainEndpointsForPart,
  userToChainPositionMm,
} from '../../geometry/positionFrame.js';
import type { NormalizedXtsConfig } from '../../geometry/normalizeXtsConfig.js';
import { INFEED_MODULE_TYPES } from '../../geometry/ModuleCatalog.js';
import type {
  ModuleHighlight,
  ModuleRef,
  MoverRef,
  PartConfig,
  ProcessingUnitConfig,
  TrackTransform,
  XtsConfig,
  XtsRuntimeState,
} from '../../types.js';

/** Default tint for the `highlightInfeedModules` convenience boolean. */
export const DEFAULT_INFEED_HIGHLIGHT_COLOR = '#FF6600';

/**
 * Merge the declarative `moduleHighlights` prop with the
 * `highlightInfeedModules` convenience boolean into the single
 * `moduleHighlights` array the renderer consumes.
 *
 * Explicit entries come first so they keep priority on overlapping module
 * types (downstream: first matching entry per type wins). Returns `undefined`
 * when nothing should be highlighted, so callers can omit the prop entirely.
 */
export function resolveModuleHighlights(
  moduleHighlights: ModuleHighlight[] | undefined,
  highlightInfeedModules: boolean | undefined,
  infeedHighlightColor: string | undefined,
): ModuleHighlight[] | undefined {
  const base = moduleHighlights ?? [];
  if (!highlightInfeedModules) {
    return base.length > 0 ? base : undefined;
  }
  return [
    ...base,
    {
      moduleTypes: [...INFEED_MODULE_TYPES],
      color: infeedHighlightColor ?? DEFAULT_INFEED_HIGHLIGHT_COLOR,
    },
  ];
}

/**
 * AABB of the rendered scene. Samples each part chain (≥ 20 / ≥ 5×modules
 * samples) and applies the per-XPU `trackTransform` so the box matches the
 * actual world geometry. Used by `zoomToFit` and the `top-down` screenshot
 * mode.
 */
export function computeBoundingBox(
  config: NormalizedXtsConfig,
  trackOverrides?: Record<number, TrackTransform>,
): Box3 {
  const box = new Box3();
  const tmpVec = new Vector3();
  for (const xpu of config.processingUnits) {
    const tt = trackOverrides?.[xpu.objectId] ?? xpu.trackTransform ?? null;
    if (tt && tt.visible === false) continue;
    const trackM = trackTransformMatrix(tt);
    for (const part of xpu.parts) {
      // Skip parts that the renderer would hide.
      if (part.partTransformation?.visible === false) continue;
      // Compose track ⊗ part so the bbox tracks lifts / live-moved parts.
      const partM = partTransformationMatrix(part.partTransformation);
      const m = new Matrix4().copy(trackM).multiply(partM);
      const chain = buildChain(part.modules);
      const samples = Math.max(20, chain.modules.length * 5);
      for (let i = 0; i <= samples; i++) {
        const s = (i / samples) * chain.trackLengthMm;
        const pose = moverWorldAt(chain, s);
        if (pose) {
          tmpVec.set(
            pose.positionMm[0],
            pose.positionMm[1],
            pose.positionMm[2],
          );
          tmpVec.applyMatrix4(m);
          box.expandByPoint(tmpVec);
        }
      }
    }
  }
  // Margin so movers/tools aren't flush with the AABB edge.
  if (!box.isEmpty()) {
    box.expandByScalar(200);
  }
  return box;
}

// ─── Orthographic top-down framing ───────────────────────────────────────────

/** Near plane for the top-down ortho camera (mm). */
export const TOP_DOWN_NEAR = 1;
/** Far plane for the top-down ortho camera (mm). */
export const TOP_DOWN_FAR = 1_000_000;
/** Extra clearance added above the scene so nothing clips the near plane (mm). */
export const TOP_DOWN_CAMERA_LIFT_MM = 1000;

/**
 * Fully-resolved orthographic top-down camera framing. The shared source of
 * truth for both the live 2D view (`projection='orthographic'`) and the
 * `exportScreenshot({ mode: 'top-down' })` path, so the two stay pixel-
 * consistent.
 */
export interface TopDownOrtho {
  /** Camera position: directly above the AABB centre on +Z. */
  position: [number, number, number];
  /** Camera up vector — always world +Y (well-defined looking straight down). */
  up: [number, number, number];
  left: number;
  right: number;
  top: number;
  bottom: number;
  near: number;
  far: number;
  /** Look-at target = AABB centre. */
  target: [number, number, number];
}

/**
 * Compute the orthographic frustum + pose that frames `bbox` straight from
 * above (+Z), up = +Y, fitted to `viewportAspect` (width / height) plus a
 * `paddingFactor` margin. Mirrors the math in the screenshot `'top-down'`
 * branch exactly.
 */
export function computeTopDownOrtho(
  bbox: Box3,
  viewportAspect: number,
  paddingFactor = 1.1,
): TopDownOrtho {
  const center = bbox.getCenter(new Vector3());
  const size = bbox.getSize(new Vector3());
  const sx = Math.max(1, size.x);
  const sy = Math.max(1, size.y);
  const sz = Math.max(1, size.z);
  const aspect =
    Number.isFinite(viewportAspect) && viewportAspect > 0 ? viewportAspect : 1;

  // Pick the dominant frustum axis so the entire bbox fits inside.
  let halfW = (sx * paddingFactor) / 2;
  let halfH = (sy * paddingFactor) / 2;
  if (halfW / halfH < aspect) halfW = halfH * aspect;
  else halfH = halfW / aspect;

  const camZ = center.z + sz / 2 + TOP_DOWN_CAMERA_LIFT_MM;
  return {
    position: [center.x, center.y, camZ],
    up: [0, 1, 0],
    left: -halfW,
    right: halfW,
    top: halfH,
    bottom: -halfH,
    near: TOP_DOWN_NEAR,
    far: TOP_DOWN_FAR,
    target: [center.x, center.y, center.z],
  };
}

// ─── Scoped bounding boxes (for `focusOn`) ───────────────────────────────────

/** Clearance added around a focused object so it isn't flush with the frame. */
const FOCUS_MARGIN_MM = 150;
/** Approximate mover footprint half-extent used when framing a single mover. */
const MOVER_HALF_EXTENT_MM = 175;

/** world = orientation ⊗ trackTransform ⊗ partTransformation for one part. */
function partWorldMatrix(
  config: XtsConfig,
  xpu: ProcessingUnitConfig,
  part: PartConfig,
  overrides?: Record<number, TrackTransform>,
): Matrix4 {
  const tt = overrides?.[xpu.objectId] ?? xpu.trackTransform ?? null;
  return new Matrix4()
    .copy(orientationMatrix(config.orientation))
    .multiply(trackTransformMatrix(tt))
    .multiply(partTransformationMatrix(part.partTransformation));
}

/** Find the XPU + part owning the given part `globalNumber`. */
function findPartByGlobalNumber(
  config: NormalizedXtsConfig,
  globalNumber: number,
): { xpu: ProcessingUnitConfig; part: PartConfig } | null {
  for (const xpu of config.processingUnits) {
    const part = xpu.parts.find((p) => p.globalNumber === globalNumber);
    if (part) return { xpu, part };
  }
  return null;
}

/**
 * AABB of a track region spanning `partOids` from `startUser`..`endUser`
 * (user-frame positions). Mirrors the segment math used by <XtsStations> /
 * <XtsAreas>: the region is anchored to the first partOid's transform and
 * each partOid contributes a sampled polyline.
 */
function regionBoundingBox(
  config: NormalizedXtsConfig,
  partOids: number[],
  startUser: number,
  endUser: number,
  overrides?: Record<number, TrackTransform>,
): Box3 {
  const box = new Box3();
  const anchorOid = partOids[0];
  if (anchorOid === undefined) return box;
  const anchor = findPartByGlobalNumber(config, anchorOid);
  if (!anchor) return box;
  const { xpu, part: anchorPart } = anchor;
  const m = partWorldMatrix(config, xpu, anchorPart, overrides);
  const pf = xpu.positionFrame;

  // Build chains for this XPU keyed by globalNumber.
  const chainsByGlobal = new Map(
    xpu.parts.map((p) => [p.globalNumber, buildChain(p.modules)] as const),
  );

  // Closed single-part loop (end < start) → duplicate the partOid, matching
  // the renderer's segment closure.
  const parts =
    partOids.length === 1 && endUser < startUser
      ? [partOids[0]!, partOids[0]!]
      : partOids;

  for (let i = 0; i < parts.length; i++) {
    const chain = chainsByGlobal.get(parts[i]!);
    if (!chain) continue;
    const isFirst = i === 0;
    const isLast = i === parts.length - 1;
    const ep = chainEndpointsForPart(chain.trackLengthMm, pf);
    const start = isFirst
      ? userToChainPositionMm(startUser, pf)
      : ep.userStartChainMm;
    const end = isLast
      ? userToChainPositionMm(endUser, pf)
      : ep.userEndChainMm;
    for (const p of sampleChainRange(chain, start, end, 20)) {
      box.expandByPoint(p.applyMatrix4(m));
    }
  }
  if (!box.isEmpty()) box.expandByScalar(FOCUS_MARGIN_MM);
  return box;
}

/** World AABB of a station (by `stationId`). Empty box if not found. */
export function computeStationBoundingBox(
  config: NormalizedXtsConfig,
  stationId: number,
  overrides?: Record<number, TrackTransform>,
): Box3 {
  const s = config.stations?.find((st) => st.stationId === stationId);
  if (!s) return new Box3();
  return regionBoundingBox(
    config,
    s.partOids,
    s.startPositionOnPart,
    s.endPositionOnPart,
    overrides,
  );
}

/** World AABB of an area (by `areaId`). Empty box if not found. */
export function computeAreaBoundingBox(
  config: NormalizedXtsConfig,
  areaId: number,
  overrides?: Record<number, TrackTransform>,
): Box3 {
  const a = config.areas?.find((ar) => ar.areaId === areaId);
  if (!a) return new Box3();
  return regionBoundingBox(
    config,
    a.partOids,
    a.startPositionOnPart,
    a.endPositionOnPart,
    overrides,
  );
}

/** World AABB of a single module (by `ModuleRef`). Empty box if not found. */
export function computeModuleBoundingBox(
  config: NormalizedXtsConfig,
  ref: ModuleRef,
  overrides?: Record<number, TrackTransform>,
): Box3 {
  const box = new Box3();
  const xpu = config.processingUnits.find(
    (x) => x.objectId === ref.processingUnitObjectId,
  );
  if (!xpu) return box;
  const part = xpu.parts.find((p) => p.objectId === ref.partObjectId);
  if (!part) return box;
  const chain = buildChain(part.modules);
  const mod = chain.modules[ref.moduleIndex];
  if (!mod) return box;
  const m = partWorldMatrix(config, xpu, part, overrides);
  const start = mod.startPositionInPartMm;
  const end = start + mod.trackLengthMm;
  for (const p of sampleChainRange(chain, start, end, 10)) {
    box.expandByPoint(p.applyMatrix4(m));
  }
  if (!box.isEmpty()) box.expandByScalar(FOCUS_MARGIN_MM);
  return box;
}

/**
 * World AABB of a single mover (by `MoverRef`). Uses the live position from
 * `getLivePositionMm(moverId)` when provided, else the config-time position.
 * Empty box if not found.
 */
export function computeMoverBoundingBox(
  config: NormalizedXtsConfig,
  ref: MoverRef,
  overrides?: Record<number, TrackTransform>,
  getLivePositionMm?: (moverId: number) => number | undefined,
): Box3 {
  const box = new Box3();
  const xpu = config.processingUnits.find(
    (x) => x.objectId === ref.processingUnitObjectId,
  );
  if (!xpu) return box;
  const mover = xpu.movers.find((m) => m.index === ref.moverIndex);
  if (!mover) return box;
  const part = xpu.parts.find((p) => p.globalNumber === mover.partOid);
  if (!part) return box;
  const userPos = getLivePositionMm?.(mover.id) ?? mover.partPositionMm;
  const chainPos = userToChainPositionMm(userPos, xpu.positionFrame);
  const pose = moverWorldAt(buildChain(part.modules), chainPos);
  if (!pose) return box;
  const m = partWorldMatrix(config, xpu, part, overrides);
  const p = new Vector3(
    pose.positionMm[0],
    pose.positionMm[1],
    pose.positionMm[2],
  ).applyMatrix4(m);
  box.setFromCenterAndSize(
    p,
    new Vector3(
      MOVER_HALF_EXTENT_MM * 2,
      MOVER_HALF_EXTENT_MM * 2,
      MOVER_HALF_EXTENT_MM * 2,
    ),
  );
  return box;
}

export function resolveToneMapping(
  mode: 'aces' | 'linear' | 'reinhard' | 'cineon' | 'agx' | 'none' | undefined,
): ToneMapping {
  switch (mode) {
    case 'linear':
      return LinearToneMapping;
    case 'reinhard':
      return ReinhardToneMapping;
    case 'cineon':
      return CineonToneMapping;
    case 'agx':
      return AgXToneMapping;
    case 'none':
      return NoToneMapping;
    case 'aces':
    default:
      return ACESFilmicToneMapping;
  }
}

/** Snapshot of runtime-only mover state for `exportModel` / `getRuntimeState`. */
export function buildRuntimeState(config: XtsConfig): XtsRuntimeState {
  const movers: XtsRuntimeState['movers'] = [];
  for (const xpu of config.processingUnits) {
    for (const m of xpu.movers) {
      movers.push({
        processingUnitObjectId: xpu.objectId,
        moverIndex: m.index,
        id: m.id,
        partOid: m.partOid,
        partPositionMm: m.partPositionMm,
        ...(m.status ? { status: m.status } : {}),
      });
    }
  }
  return { movers };
}
